import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { generateTokenWithSignature } from '../utils/tokenGenerator';
import { userMemberApi, userMenuApi, INTEGRATED_BASE_URL, RINJANI_BASE_URL } from '../config/api';

const AuthContext = createContext();
const SESSION_IDLE_TIMEOUT_MS = 6 * 60 * 60 * 1000;
const SESSION_LAST_ACTIVITY_KEY = 'sessionLastActivity';

// Menu master data still contains these legacy URLs for some assignments, while
// the application now exposes both features inside the Product Manager workspace.
// Canonicalizing them here keeps existing and newly-created assignments valid
// without granting access to any menu that was not explicitly assigned.
const LEGACY_PATH_ALIASES = new Map([
  ['/my-bundling-package', '/package-manager/my-packages'],
  ['/browse-inventory', '/package-manager/product-catalog'],
  // Overview is an additive landing page, not a new privilege boundary. Existing
  // My Products assignments may open it without a master-menu reassignment.
  ['/package-manager/overview', '/package-manager/my-packages'],
  ['/product-monitoring/packages', '/product-monitoring/products'],
  ['/product-monitoring/airports', '/product-monitoring/products'],
  ['/product-monitoring/aviation-services', '/product-monitoring/products'],
  ['/product-monitoring/hospitality', '/product-monitoring/products'],
  ['/product-monitoring/destination-management', '/product-monitoring/products'],
  ['/product-monitoring/retail', '/product-monitoring/products'],
  ['/product-monitoring/tourism-development', '/product-monitoring/products'],
]);

// These routes are tabs inside an assigned Product Workspace, not standalone
// sidebar menus. Access to the member's Bundling Product page therefore also
// grants access to its supporting import/content tabs.
const PRODUCT_WORKSPACE_PARENT_PATHS = new Map([
  ['/aviation-services/integrationData', '/aviation-services/bundling-product'],
  ['/destination-management/integrationData', '/destination-management/bundling-product'],
  ['/destination-management/goers-content', '/destination-management/bundling-product'],
  ['/tourism-development/goers-content', '/tourism-development/bundling-product'],
]);

const normalizeAdminStatus = (user, selectedRole = null) => {
  const raw = selectedRole || user?.admin_status || user?.adminStatus || user?.role || '';
  return String(raw).trim().toLowerCase().replace(/[\s-]+/g, '_');
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Single source of truth for user-menu permission checking. Used by both
// Sidebar (to filter visible nav) and ProtectedRoute (to gate routes).
// All paths are normalized to leading slash without `/admin` prefix to match
// the shape returned by the userMenu API.
const normalizeAllowedPath = (path) => {
  if (!path) return '';
  let normalized = String(path).trim();
  if (!normalized) return '';
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    try {
      const u = new URL(normalized);
      normalized = u.pathname || '/';
    } catch {
      return '';
    }
  }
  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  normalized = normalized.split('?')[0].split('#')[0];
  if (normalized.startsWith('/admin')) {
    normalized = normalized.replace('/admin', '') || '/';
  }
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }
  return LEGACY_PATH_ALIASES.get(normalized) || normalized;
};

const URL_FIELD_KEYS = [
  'MENU_URL',
  'menu_url',
  'MENUSUBCATEGORY_URL',
  'menusubcategory_url',
  'MENU_DEV_URL',
  'menu_dev_url',
];

const addUrlFieldsToSet = (row, set) => {
  if (!row || typeof row !== 'object') return;
  for (const key of URL_FIELD_KEYS) {
    const raw = row[key];
    if (raw && typeof raw === 'string') {
      const path = normalizeAllowedPath(raw);
      if (path) set.add(path);
    }
  }
};

/** Flatten API payload: array, or common wrapper shapes. */
const extractMenuRows = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.menus)) return data.menus;
  return [data];
};

const buildAllowedPathSet = (menus = []) => {
  const set = new Set();
  const rows = extractMenuRows(menus);
  const visit = (row, depth = 0) => {
    if (!row || typeof row !== 'object' || depth > 8) return;
    addUrlFieldsToSet(row, set);
    const nestedKeys = ['menus', 'MENU', 'menu', 'children', 'subMenus', 'submenus', 'MENUS'];
    for (const nk of nestedKeys) {
      const child = row[nk];
      if (Array.isArray(child)) {
        child.forEach((c) => visit(c, depth + 1));
      } else if (child && typeof child === 'object') {
        visit(child, depth + 1);
      }
    }
  };
  rows.forEach((row) => visit(row, 0));
  return set;
};

const ALWAYS_ALLOWED_PATHS = new Set([
  '/',
  '/ieem-dashboard',
  '/product-dashboard',
  '/product-monitoring/packages',
  '/product-monitoring/overview',
  '/product-monitoring/products',
  '/product-monitoring/reservations',
  '/product-monitoring/channels',
  '/product-monitoring/members',
  '/product-monitoring/airports',
  '/product-monitoring/aviation-services',
  '/product-monitoring/hospitality',
  '/product-monitoring/destination-management',
  '/product-monitoring/retail',
  '/product-monitoring/tourism-development',
]);

const isHinWorkspacePath = (path) =>
  path === '/hospitality/hotel-master' || path === '/hospitality/availability';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const auth = localStorage.getItem('isAuthenticated') === 'true';
    const userData = localStorage.getItem('userData');
    // Prevent inconsistent state: if auth=true but userData is missing, force logout
    if (auth && !userData) {
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      localStorage.removeItem('integratedToken');
      localStorage.removeItem('selectedRole');
      return false;
    }
    return auth;
  });

  const [user, setUser] = useState(() => {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  });

  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || null;
  });

  const [integratedToken, setIntegratedToken] = useState(() => {
    return localStorage.getItem('integratedToken') || null;
  });

  const [selectedRole, setSelectedRole] = useState(() => {
    return localStorage.getItem('selectedRole') || null;
  });

  const [allowedPaths, setAllowedPaths] = useState(() => new Set());
  // A restored login must wait for its menu request before ProtectedRoute decides.
  // Starting at false caused a brief (and sometimes persistent) Access Denied screen
  // while allowedPaths was still empty after a reload.
  const [isMenuLoading, setIsMenuLoading] = useState(() => Boolean(user?.nip));

  const refreshUserProfile = useCallback(async (nip) => {
    if (!nip) return null;
    try {
      const response = await userMemberApi.get(`/${nip}`);
      const latest = response.data?.data || response.data;
      if (!latest || !latest.nip) return null;
      setUser((current) => {
        const merged = { ...(current || {}), ...latest };
        localStorage.setItem('userData', JSON.stringify(merged));
        return merged;
      });
      return latest;
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  }, []);

  const refreshMenu = useCallback(async (nip) => {
    const targetNip = nip || user?.nip;
    if (!targetNip) {
      setAllowedPaths(new Set());
      return;
    }
    setIsMenuLoading(true);
    try {
      const response = await userMenuApi.get(`/list/${targetNip}`);
      if (response.data?.status === 200) {
        setAllowedPaths(buildAllowedPathSet(response.data.data || []));
      } else {
        setAllowedPaths(new Set());
      }
    } catch (error) {
      console.error('Error fetching user menu permissions:', error);
      setAllowedPaths(new Set());
    } finally {
      setIsMenuLoading(false);
    }
  }, [user?.nip]);

  useEffect(() => {
    if (!user?.nip) {
      setAllowedPaths(new Set());
      setIsMenuLoading(false);
      return;
    }
    refreshUserProfile(user.nip);
    refreshMenu(user.nip);
  }, [user?.nip, refreshMenu, refreshUserProfile]);

  // Menu assignments can be changed by an administrator while this user still has
  // an open session. Refresh when the tab becomes active again so the user does not
  // have to clear storage or sign in again to receive the newly assigned access.
  useEffect(() => {
    if (!user?.nip) return undefined;
    const refreshActiveSessionMenu = () => {
      if (document.visibilityState === 'visible') {
        refreshUserProfile(user.nip);
        refreshMenu(user.nip);
      }
    };
    window.addEventListener('focus', refreshActiveSessionMenu);
    document.addEventListener('visibilitychange', refreshActiveSessionMenu);
    return () => {
      window.removeEventListener('focus', refreshActiveSessionMenu);
      document.removeEventListener('visibilitychange', refreshActiveSessionMenu);
    };
  }, [user?.nip, refreshMenu, refreshUserProfile]);

  const isPathAllowed = useCallback((path) => {
    if (!path) return false;
    const normalized = normalizeAllowedPath(path);
    if (!normalized) return false;
    const adminStatus = normalizeAdminStatus(user, selectedRole);
    const accountAdminStatus = normalizeAdminStatus(user);

    // Admin INJ owns the internal operational tools even when the menu-assignment
    // endpoint has not returned explicit Internal Tools rows for the account.
    if ((accountAdminStatus === 'admin_inj' || adminStatus === 'admin_inj') && (
      normalized === '/internal-tools' || normalized.startsWith('/internal-tools/')
    )) {
      return true;
    }

    // Admin is an organization member and remains bounded by assigned menus.
    // Home and workspace dashboards are always available to authenticated users.
    if (ALWAYS_ALLOWED_PATHS.has(normalized) || normalized === '') return true;
    // Detail pages inherit access from their workspace/list route. They are not
    // separate menu assignments, so `/products/:id` must remain accessible when
    // `/products` is an always-available or assigned Product Monitoring menu.
    for (const alwaysAllowed of ALWAYS_ALLOWED_PATHS) {
      if (alwaysAllowed !== '/' && normalized.startsWith(`${alwaysAllowed}/`)) return true;
    }
    if (allowedPaths.has(normalized)) return true;

    const workspaceParent = PRODUCT_WORKSPACE_PARENT_PATHS.get(normalized);
    if (workspaceParent && allowedPaths.has(workspaceParent)) return true;

    for (const allowed of allowedPaths) {
      if (!allowed || allowed === '/') continue;
      if (normalized === allowed || normalized.startsWith(`${allowed}/`)) {
        return true;
      }
      if (allowed.startsWith(`${normalized}/`)) {
        return true;
      }
    }
    return false;
  }, [allowedPaths, selectedRole, user]);

  const login = async (nik, password) => {
    try {
      // Step 1: Check user member to determine login type
      let userMemberData = null;
      try {
        const checkResponse = await axios.get(
          `${INTEGRATED_BASE_URL}/usermember/check/${nik}`,
          {
            timeout: 10000,
          }
        );
        
        if (checkResponse.data && checkResponse.data.exists) {
          // Prefer nested `data` (canonical); fall back to top-level login_type for older API shapes
          userMemberData = checkResponse.data.data || {
            login_type: checkResponse.data.login_type ?? null,
          };
        }
      } catch (checkError) {
        console.warn('Error checking user member:', checkError);
      }
      
      // Step 2: Determine login type and perform appropriate login
      const loginType = userMemberData?.login_type ?? null;
      
      let response;
      
      if (loginType === '0') {
        // Login lokal (tanpa rinjani)
        response = await axios.post(
          `${INTEGRATED_BASE_URL}/usermember/login-local`,
          {
            nik,
            password
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
      } else {
        // Login rinjani (default)
        const formData = new FormData();
        formData.append('nik', nik);
        formData.append('password', password);

        const config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: `${RINJANI_BASE_URL}/login`,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          data: formData
        };

        response = await axios.request(config);
      }

      // Handle response based on login type
      const isSuccess = loginType === '0' 
        ? response.data.success === true || response.data.status === 200
        : response.data.success === true;
      
      if (isSuccess) {
        let authToken, userData;
        
        if (loginType === '0') {
          // Login lokal response format
          authToken = response.data.token || 'local-login-token';
          userData = {
            nip: response.data.nip,
            name: response.data.name,
            position_name: response.data.position_name,
            organization_code: response.data.organization_code,
            organization_name: response.data.organization_name,
            unit_name: response.data.unit_name,
            group_name: response.data.group_name,
            division_name: response.data.division_name,
            admin_status: response.data.admin_status || '',
            role: response.data.role || '',
            login_type: response.data.login_type || '0'
          };
        } else {
          // Login rinjani response format
          const { token, ...restData } = response.data;
          authToken = token;
          userData = restData;
        }
        
        // Generate token untuk integrated.injourney.id API
        // Menggunakan cara yang sama seperti di integrated.injourney.id
        let integratedApiToken = null;
        try {
          integratedApiToken = generateTokenWithSignature(userData);
        } catch (error) {
          console.error('Error generating integrated token:', error);
          // Fallback: gunakan token dari login response jika generate gagal
          integratedApiToken = authToken;
        }
        
        // Save token terlebih dahulu untuk bisa digunakan di API call
        localStorage.setItem('token', authToken);
        localStorage.setItem('integratedToken', integratedApiToken);
        
        // For rinjani login, fetch user member data untuk mendapatkan admin_status dan role
        if (loginType !== '0' && !userMemberData) {
          try {
            const nip = userData.nip || nik;
            if (nip) {
              try {
                const memberResponse = await userMemberApi.get(`/${nip}`);
                if (memberResponse.data && memberResponse.data.data) {
                  userMemberData = memberResponse.data.data;
                } else if (memberResponse.data && !memberResponse.data.data) {
                  userMemberData = memberResponse.data;
                }
              } catch (memberError) {
                console.warn('Error fetching user member by NIP:', memberError);
                try {
                  const listResponse = await userMemberApi.get('/list');
                  if (listResponse.data && listResponse.data.data && Array.isArray(listResponse.data.data)) {
                    const foundUser = listResponse.data.data.find(u => u.nip === nip);
                    if (foundUser) {
                      userMemberData = foundUser;
                    }
                  }
                } catch (listError) {
                  console.warn('Error fetching user member list:', listError);
                }
              }
            }
          } catch (error) {
            console.warn('Error fetching user member data:', error);
          }
        }
        
        // Merge user member data (admin_status dan role) ke userData
        const mergedUserData = {
          ...userData,
          ...(userMemberData && {
            admin_status: userMemberData.admin_status || userData.admin_status || '',
            role: userMemberData.role || userData.role || '',
            login_type: userMemberData.login_type || userData.login_type || '1'
          })
        };
        
        // Save to localStorage
        localStorage.setItem('userData', JSON.stringify(mergedUserData));
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(Date.now()));

        // Debug: Log role dan admin_status setelah login
        console.log('=== LOGIN DEBUG ===');
        console.log('Login Type:', loginType === '0' ? 'Lokal' : 'Rinjani');
        console.log('mergedUserData:', mergedUserData);
        console.log('admin_status:', mergedUserData.admin_status);
        console.log('role:', mergedUserData.role);
        console.log('userData from localStorage:', JSON.parse(localStorage.getItem('userData') || '{}'));
        console.log('===================');

        // Update state
        setToken(authToken);
        setIntegratedToken(integratedApiToken);
        setUser(mergedUserData);
        setIsAuthenticated(true);

        return { success: true, data: { ...response.data, ...mergedUserData } };
      } else {
        return { 
          success: false, 
          message: loginType === '0' 
            ? (response.data.message || 'Login gagal')
            : 'Login gagal' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Terjadi kesalahan saat login'
      };
    }
  };

  const logout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setIntegratedToken(null);
    setSelectedRole(null);
    setAllowedPaths(new Set());
    setIsMenuLoading(false);
    localStorage.removeItem('token');
    localStorage.removeItem('integratedToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('selectedRole');
    localStorage.removeItem(SESSION_LAST_ACTIVITY_KEY);
    // Clear in-memory API Playground auth (never persisted by playground itself)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('prime-playground-clear-auth'));
    }
  }, []);

  // Sliding idle session: active use keeps the session alive, but six hours without
  // mouse, keyboard, touch, scroll, or focus activity logs the account out. The last
  // activity lives in localStorage so reloads and multiple tabs share one deadline.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let idleTimer;
    let lastPersisted = Number(localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) || Date.now();
    if (!localStorage.getItem(SESSION_LAST_ACTIVITY_KEY)) {
      localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(lastPersisted));
    }

    const scheduleLogout = (lastActivity = lastPersisted) => {
      window.clearTimeout(idleTimer);
      const remaining = SESSION_IDLE_TIMEOUT_MS - (Date.now() - lastActivity);
      if (remaining <= 0) {
        logout();
        return;
      }
      idleTimer = window.setTimeout(logout, remaining);
    };

    const markActivity = () => {
      const now = Date.now();
      // A focus/click after a suspended background tab must not revive a session
      // whose six-hour idle deadline already passed.
      if (now - lastPersisted >= SESSION_IDLE_TIMEOUT_MS) {
        logout();
        return;
      }
      // Limit storage churn during scroll/mouse activity while still resetting the
      // in-memory deadline immediately.
      if (now - lastPersisted >= 30000) {
        lastPersisted = now;
        localStorage.setItem(SESSION_LAST_ACTIVITY_KEY, String(now));
      }
      scheduleLogout(now);
    };

    const syncSession = (event) => {
      if (event.key === 'isAuthenticated' && event.newValue !== 'true') {
        logout();
        return;
      }
      if (event.key === SESSION_LAST_ACTIVITY_KEY && event.newValue) {
        lastPersisted = Number(event.newValue) || Date.now();
        scheduleLogout(lastPersisted);
      }
    };

    const events = ['pointerdown', 'keydown', 'touchstart', 'scroll', 'focus'];
    events.forEach((eventName) => window.addEventListener(eventName, markActivity, { passive: true }));
    window.addEventListener('storage', syncSession);
    scheduleLogout(lastPersisted);

    return () => {
      window.clearTimeout(idleTimer);
      events.forEach((eventName) => window.removeEventListener(eventName, markActivity));
      window.removeEventListener('storage', syncSession);
    };
  }, [isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      token,
      integratedToken,
      selectedRole,
      setSelectedRole,
      login,
      logout,
      allowedPaths,
      isMenuLoading,
      isPathAllowed,
      refreshMenu,
      refreshUserProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
